// Cross-supplier "never sent" — required compliance documents a supplier has
// NEVER submitted (no upload, no acknowledgement). This is the largest
// compliance gap in the system and it appears nowhere in the review queue
// today, because the queue only shows documents that EXIST. There's often no
// row at all, so it can't come from the documents table: it's each
// supplier's required set (their risk tier ∪ per-supplier overrides −
// not_required, ∩ live master docs) MINUS what they've submitted. Mirrors
// the per-supplier required-set logic in useSupplierCompliance, batched.
//
// A grace period (default 7 days, compliance-queue §4/§11) keeps a freshly
// onboarded supplier off the red list on day one: a requirement only counts
// once it has applied for longer than `graceDays`. "Required since" is the
// per-supplier onboarding-doc row's created_at where one exists, else the
// supplier's own created_at.
//
// Pure — no React, no Supabase. Caller passes the rows it already loads.
export function computeNeverSent({
  suppliers = [],
  requirements = [],
  records = [],
  acks = [],
  liveDocKeys = [],
  docMeta = {},
  graceDays = 7,
  now = Date.now(),
} = {}) {
  const liveSet = liveDocKeys instanceof Set ? liveDocKeys : new Set(liveDocKeys);

  // risk tier → [document_key]
  const byTier = new Map();
  for (const r of requirements) {
    if (!byTier.has(r.risk_level)) byTier.set(r.risk_level, []);
    byTier.get(r.risk_level).push(r.document_key);
  }

  // per-supplier onboarding rows
  const recsBySupplier = new Map();
  for (const d of records) {
    if (!recsBySupplier.has(d.supplier_id)) recsBySupplier.set(d.supplier_id, []);
    recsBySupplier.get(d.supplier_id).push(d);
  }

  // A key counts as "submitted" once there's an upload (supplier_uploaded_at
  // or signed_at) or an acknowledgement for it — a rejected upload still
  // counts (they sent something; that's the Rejected bucket, not this one).
  const submittedBySupplier = new Map();
  const addSubmitted = (sid, key) => {
    if (!submittedBySupplier.has(sid)) submittedBySupplier.set(sid, new Set());
    submittedBySupplier.get(sid).add(key);
  };
  for (const d of records) {
    if (d.supplier_uploaded_at || d.signed_at) addSubmitted(d.supplier_id, d.document_key);
  }
  for (const a of acks) {
    if (a.acknowledged_at) addSubmitted(a.supplier_id, a.document_key);
  }

  const graceMs = graceDays * 24 * 60 * 60 * 1000;
  const out = [];
  for (const s of suppliers) {
    const tierKeys = byTier.get(s.risk_flag) || [];
    const recs = recsBySupplier.get(s.id) || [];
    const recByKey = new Map(recs.map(d => [d.document_key, d]));

    const required = new Set(tierKeys);
    for (const d of recs) if (d.risk_override_applicable) required.add(d.document_key);
    for (const d of recs) if (d.not_required) required.delete(d.document_key);

    const submitted = submittedBySupplier.get(s.id) || new Set();

    for (const key of required) {
      if (!liveSet.has(key)) continue;   // phantom requirement (draft/removed master doc)
      if (submitted.has(key)) continue;  // they've sent something for it

      const rec = recByKey.get(key);

      // NEVER PUBLISHED → NOT THE SUPPLIER'S FAULT, so not their gap.
      //
      // Rob, 2026-08-14: "documents should not be flagged as not received by a
      // supplier if they have never been sent / don't have them on their log
      // in." He is right, and this was the bug behind a supplier appearing in
      // the queue's "Never sent" list for documents that had never reached
      // their portal — they were being counted against for something they had
      // never been shown.
      //
      // `sent_at` is what puts a document on the supplier's login. No row at
      // all means it was never published either.
      //
      // ⚠️ The residue this hides — required, live, but never published — is a
      // fault on OUR side, not theirs. It is meant to be kept near zero by the
      // auto-publish reconciliation on SupplierDetail, which since the same
      // date covers every risk tier rather than only low. If that ever stops
      // running, documents would go quietly unasked-for instead of loudly
      // unanswered, so the two changes belong together.
      if (!rec?.sent_at) continue;
      const sinceIso = rec?.created_at || s.created_at || null;
      const sinceMs = sinceIso ? new Date(sinceIso).getTime() : null;
      // Within grace → not yet a gap. Unknown date (old supplier) → include.
      if (sinceMs != null && !Number.isNaN(sinceMs) && (now - sinceMs) <= graceMs) continue;

      out.push({
        supplier_id: s.id,
        document_key: key,
        label: docMeta?.[key]?.label || key,
        requiredSince: sinceIso,
      });
    }
  }
  return out;
}
